// IU Calculator - IU to syringe-units conversion via vial concentration.

const LIMITS = {
  vial: { min: 0.1, max: 1000, label: 'IU' },
  bac:  { min: 0.1, max: 10,   label: 'mL' },
  dose: { min: 0.01, max: 1000, label: 'IU' },
};

function validate(field, value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return { valid: false, error: 'Enter a number' };
  }
  if (value <= 0) return { valid: false, error: 'Must be greater than 0' };
  const limit = LIMITS[field];
  if (limit && (value < limit.min || value > limit.max)) {
    return { valid: false, error: `Must be between ${limit.min} and ${limit.max} ${limit.label}` };
  }
  return { valid: true };
}

export function calculateIu({ vialIu, potencyIuPerMg, bacMl, doseIu }) {
  const vialMg = vialIu / potencyIuPerMg;
  const concentrationMgPerMl = vialMg / bacMl;
  const concentrationIuPerMl = vialIu / bacMl;
  const volumeMl = doseIu / concentrationIuPerMl;
  const units = volumeMl * 100;
  const doseMg = doseIu / potencyIuPerMg;
  return { vialMg, concentrationMgPerMl, concentrationIuPerMl, volumeMl, units, doseMg };
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
}

function boot() {
  const root = document.querySelector('.is-iu');
  if (!root) return;
  const $ = (s) => root.querySelector(s);
  const $$ = (s) => root.querySelectorAll(s);
  const empty = $('#is-iu-result-empty');
  const ok = $('#is-iu-result-ok');
  const unitsEl = $('#is-iu-headline-units');
  const sublineEl = $('#is-iu-subline');
  const convEl = $('#is-iu-conversion');
  const potencyPills = $$('[data-potency]');

  // Result syringe (shared snippet is-calc-syr): draws the pull line on a 1 mL U-100 barrel.
  const syrEl = document.getElementById('is-calc-syr');
  const syrRefs = syrEl ? {
    fluid: document.getElementById('is-calc-syr-fluid'),
    seal: document.getElementById('is-calc-syr-seal'),
    plate: document.getElementById('is-calc-syr-plate'),
    rod: document.getElementById('is-calc-syr-rod'),
    ticks: document.getElementById('is-calc-syr-ticks'),
    nums: document.getElementById('is-calc-syr-nums'),
    caption: document.getElementById('is-calc-syr-caption'),
  } : null;
  const SYR = { X: 80, W: 600, TOP: 22, SEAL_W: 14, PLATE_W: 6, ROD_END: 740 };
  function paintSyringe(capacityMl, drawnMl) {
    if (!syrRefs || !syrRefs.fluid) return;
    const frac = Math.max(0, Math.min(1, drawnMl / capacityMl));
    const sealX = SYR.X + frac * SYR.W;
    syrRefs.fluid.setAttribute('width', (sealX - SYR.X).toFixed(2));
    syrRefs.seal.setAttribute('x', sealX.toFixed(2));
    syrRefs.plate.setAttribute('x', (sealX + SYR.SEAL_W).toFixed(2));
    const rodStart = sealX + SYR.SEAL_W + SYR.PLATE_W;
    syrRefs.rod.setAttribute('x', rodStart.toFixed(2));
    syrRefs.rod.setAttribute('width', Math.max(0, SYR.ROD_END - rodStart).toFixed(2));
    const totalUnits = capacityMl * 100;
    const tickEvery = totalUnits <= 50 ? 1 : 2;
    const labelEvery = totalUnits <= 30 ? 5 : 10;
    const ticks = [];
    const nums = [];
    for (let u = 0; u <= totalUnits + 1e-6; u += tickEvery) {
      const x = SYR.X + (u / totalUnits) * SYR.W;
      const isLabel = Math.abs((u / labelEvery) - Math.round(u / labelEvery)) < 1e-6;
      const h = isLabel ? 7 : 3.5;
      ticks.push('<line class="is-calc__sx-tick" x1="' + x.toFixed(2) + '" y1="' + SYR.TOP + '" x2="' + x.toFixed(2) + '" y2="' + (SYR.TOP + h).toFixed(2) + '" stroke-width="' + (isLabel ? 1.2 : 0.6) + '"/>');
      if (u > 0 && isLabel) nums.push('<text class="is-calc__sx-num" x="' + x.toFixed(2) + '" y="71" font-size="11">' + Math.round(u) + '</text>');
    }
    syrRefs.ticks.innerHTML = ticks.join('');
    syrRefs.nums.innerHTML = nums.join('');
  }
  if (syrRefs) {
    paintSyringe(1, 0);
    syrRefs.caption.textContent = 'Pick your values to see the pull line to draw.';
  }

  const state = { vial: null, bac: null, dose: null, potency: 3 };

  function recompute() {
    if (!isFinite(state.vial) || state.vial <= 0 ||
        !isFinite(state.bac) || state.bac <= 0 ||
        !isFinite(state.dose) || state.dose <= 0 ||
        !isFinite(state.potency) || state.potency <= 0) {
      empty.hidden = false; ok.hidden = true;
      paintSyringe(1, 0);
      if (syrRefs) syrRefs.caption.textContent = 'Pick your values to see the pull line to draw.';
      writeUrl();
      if (window.announceCalcResult) window.announceCalcResult(null);
      return;
    }
    const r = calculateIu({ vialIu: state.vial, potencyIuPerMg: state.potency, bacMl: state.bac, doseIu: state.dose });
    unitsEl.textContent = r.units.toFixed(r.units < 10 ? 1 : 0);
    sublineEl.textContent = '= ' + r.volumeMl.toFixed(2) + ' mL of ' + r.concentrationIuPerMl.toFixed(1) + ' IU/mL solution';
    convEl.textContent = state.dose + ' IU = ' + r.doseMg.toFixed(3) + ' mg at ' + state.potency + ' IU/mg potency. Vial holds ' + r.vialMg.toFixed(2) + ' mg total.';
    paintSyringe(1, r.volumeMl);
    if (syrRefs) syrRefs.caption.textContent = 'Draw to ' + r.units.toFixed(r.units < 10 ? 1 : 0) + ' units on a 1 mL U-100 insulin syringe';
    writeUrl();
    empty.hidden = true; ok.hidden = false;
    if (window.announceCalcResult) window.announceCalcResult(r.volumeMl);
  }

  // Pill groups with Other fallback (vial / bac / dose)
  ['vial', 'bac', 'dose'].forEach(field => {
    const pills = $$(`.is-calc__pill[data-field="${field}"]`);
    const otherBlock = root.querySelector(`.is-calc__other-input[data-field="${field}"]`);
    const otherInput = otherBlock ? otherBlock.querySelector('input') : null;

    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const value = pill.dataset.value;
        pills.forEach(p => {
          p.classList.remove('is-calc__pill--active');
          p.setAttribute('aria-checked', 'false');
        });
        pill.classList.add('is-calc__pill--active');
        pill.setAttribute('aria-checked', 'true');
        if (value === 'other') {
          if (otherBlock) otherBlock.hidden = false;
          if (otherInput) {
            otherInput.focus();
            const v = parseFloat(otherInput.value);
            state[field] = isFinite(v) && v > 0 ? v : null;
          } else {
            state[field] = null;
          }
        } else {
          if (otherBlock) {
            otherBlock.hidden = true;
            if (otherInput) otherInput.value = '';
          }
          state[field] = parseFloat(value);
        }
        recompute();
      });
    });

    const errEl = otherBlock ? otherBlock.querySelector('.is-calc__error') : null;
    if (otherInput) {
      otherInput.addEventListener('input', () => {
        const raw = otherInput.value.trim();
        if (!raw) {
          state[field] = null;
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
          recompute();
          return;
        }
        const v = parseFloat(raw);
        const check = validate(field, v);
        if (check.valid) {
          state[field] = v;
          if (errEl) { errEl.textContent = ''; errEl.hidden = true; }
        } else {
          state[field] = null;
          if (errEl) { errEl.textContent = check.error; errEl.hidden = false; }
        }
        recompute();
      });
    }
  });

  // Potency pills (no Other - the 3 standard values cover everything)
  potencyPills.forEach(p => {
    p.addEventListener('click', () => {
      const v = parseFloat(p.dataset.potency);
      if (!isFinite(v) || v <= 0) return;
      state.potency = v;
      potencyPills.forEach(other => {
        const active = other === p;
        other.classList.toggle('is-calc__pill--active', active);
        other.setAttribute('aria-checked', active ? 'true' : 'false');
      });
      recompute();
    });
  });

  // --- Shareable URL state: reflect inputs in the URL, prefill from it on load ---
  function writeUrl() {
    if (!window.history || !window.history.replaceState) return;
    const params = new URLSearchParams();
    if (isFinite(state.vial) && state.vial > 0) params.set('vial', state.vial);
    if (isFinite(state.bac) && state.bac > 0) params.set('bac', state.bac);
    if (isFinite(state.dose) && state.dose > 0) params.set('dose', state.dose);
    if (isFinite(state.potency) && state.potency > 0 && state.potency !== 3) params.set('potency', state.potency);
    const qs = params.toString();
    window.history.replaceState(null, '', window.location.pathname + (qs ? '?' + qs : ''));
  }
  function readUrlPrefill() {
    const p = new URLSearchParams(window.location.search);
    ['vial', 'bac', 'dose'].forEach(function (f) {
      const raw = p.get(f);
      if (raw === null) return;
      const v = parseFloat(raw);
      if (!isFinite(v) || v <= 0) return;
      let matched = false;
      root.querySelectorAll('.is-calc__pill[data-field="' + f + '"]').forEach(function (pill) {
        if (pill.dataset.value === String(v)) { pill.click(); matched = true; }
      });
      if (!matched) {
        const otherPill = root.querySelector('.is-calc__pill--other[data-field="' + f + '"]');
        const otherInput = root.querySelector('.is-calc__other-input[data-field="' + f + '"] input');
        if (otherPill) otherPill.click();
        if (otherInput) { otherInput.value = String(v); otherInput.dispatchEvent(new Event('input', { bubbles: true })); }
      }
    });
    const pot = p.get('potency');
    if (pot !== null) {
      const pv = parseFloat(pot);
      if (isFinite(pv) && pv > 0) {
        potencyPills.forEach(function (pill) {
          if (parseFloat(pill.dataset.potency) === pv) pill.click();
        });
      }
    }
  }
  readUrlPrefill();
}
// build 1777524650
